import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import '@cloudscape-design/global-styles/index.css';
import './styles/logo.css';
import './config/environment'; // Load and log environment config
import App from './App';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  },
});

// Simple localStorage persister with error handling
const persister = {
  persistClient: async (client: unknown) => {
    try {
      const serialized = JSON.stringify(client);
      // Only cache if under 5MB
      if (serialized.length < 5 * 1024 * 1024) {
        localStorage.setItem('REACT_QUERY_OFFLINE_CACHE', serialized);
      }
    } catch (error) {
      // Silently fail if quota exceeded or other errors
      console.warn('Failed to persist cache:', error);
    }
  },
  restoreClient: async () => {
    try {
      const cached = localStorage.getItem('REACT_QUERY_OFFLINE_CACHE');
      return cached ? JSON.parse(cached) : undefined;
    } catch (error) {
      console.warn('Failed to restore cache:', error);
      return undefined;
    }
  },
  removeClient: async () => {
    try {
      localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');
    } catch (error) {
      console.warn('Failed to remove cache:', error);
    }
  },
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </PersistQueryClientProvider>
  </React.StrictMode>
);
